import React, { useEffect, useState, useContext } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalCloseButton,
  Accordion,
  Flex,
  Box,
  Button,
  Tabs,
  TabList,
  Tab,
  TabPanel,
  TabPanels,
  Text,
  useToast,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import HelpIcon from "../InvestmentScreening/HelpIcon";
import AssignAccordionItem from './AssignAccordionItem';
import AssignSelector, { getAssignEmployee } from './AssignSelector';
import { WebAPIContext } from '../../contexts/WebAPIContext';
import { Employee } from '../../contexts/types';
import { ComfirmDivisions, PurposeType } from '../../contexts/constants';

/** 担当者選択対象部署 */
interface AssignTargetType {
  [id: string]: {
    /** idで指定した部署に属する組織分類ID（organization_class_id）を定義 */
    organizationClassIds?: string[],
    /** idで指定した部署に属する親組織ID（parent_organization_id）を定義 */
    parentOrganizationIds?: string[],
    /** idで指定した部署の子孫組織に含まれる組織分類ID（descendant_organization_class_ids）を定義 */
    descendantOrganizationClassIds?: string[],
    /** 部署名 */
    divisionName?: string,
    /** 選択した従業員情報 */
    selectedEmployees?: Employee[],
    /** 対応するフローを定義（WorkflowId.code を設定。各部署で確認が必要な画面を設定する。） */
    flowIds?: string[],
  }
}

/** 担当者設定対象（初期値）- 新製品 */
const defaultAssignTargetNewProduct: AssignTargetType = {
  // 事業部確認
  [ComfirmDivisions.companyDivision]: { descendantOrganizationClassIds: ["BI00002"], divisionName: "事業部", selectedEmployees: [], flowIds: ["2", "3", "4", "5", "6", "7", "8", "9", "10", "12"] },
  // 事業管理確認
  [ComfirmDivisions.companyHeadDivision]: { parentOrganizationIds: ["minebeamitsumi"], divisionName: "事業管理", selectedEmployees: [], flowIds: ["5", "8", "9", "10", "12"] },
  // 営業担当者確認
  [ComfirmDivisions.salesDivision]: { descendantOrganizationClassIds: ["SA00003"], divisionName: "営業部", selectedEmployees: [], flowIds: ["2", "3", "4", "5", "12"] },
  // 調達担当者確認
  [ComfirmDivisions.procurementDivision]: { descendantOrganizationClassIds: ["PL00001"], divisionName: "調達部", selectedEmployees: [], flowIds: ["3", "5", "6", "7", "12"] },
};

/** 担当者設定対象（初期値）- 増産、合理化、更新・その他 */
const defaultAssignTargetOther: AssignTargetType = {
  // 事業部確認
  [ComfirmDivisions.companyDivision]: { descendantOrganizationClassIds: ["BI00002"], divisionName: "事業部", selectedEmployees: [], flowIds: ["2", "3", "4", "5", "6", "7", "8", "9", "11", "12"] },
  // 事業管理確認
  [ComfirmDivisions.companyHeadDivision]: { parentOrganizationIds: ["minebeamitsumi"], divisionName: "事業管理", selectedEmployees: [], flowIds: ["5", "8", "9", "11", "12"] },
  // 営業担当者確認
  [ComfirmDivisions.salesDivision]: { descendantOrganizationClassIds: ["SA00003"], divisionName: "営業部", selectedEmployees: [], flowIds: ["2", "3", "4", "5", "12"] },
  // 調達担当者確認
  [ComfirmDivisions.procurementDivision]: { descendantOrganizationClassIds: ["PL00001"], divisionName: "調達部", selectedEmployees: [], flowIds: ["3", "5", "6", "7", "12"] },
};

/**
 * 担当者設定対象（初期値）- 振り返りモード用
 * 振り返りモード時のみ表示される3画面（13:営業視点 / 14:事業視点 / 15:今後の見込み）に対する
 * 確認部署のマッピング。目的（新製品/その他）によらず同一ルールを適用する。
 */
const defaultAssignTargetReview: AssignTargetType = {
  // 事業部確認: 振り返り3画面すべて
  [ComfirmDivisions.companyDivision]: { descendantOrganizationClassIds: ["BI00002"], divisionName: "事業部", selectedEmployees: [], flowIds: ["13", "14", "15"] },
  // 事業管理確認: 事業視点・今後の見込み
  [ComfirmDivisions.companyHeadDivision]: { parentOrganizationIds: ["minebeamitsumi"], divisionName: "事業管理", selectedEmployees: [], flowIds: ["14", "15"] },
  // 営業担当者確認: 営業視点・今後の見込み
  [ComfirmDivisions.salesDivision]: { descendantOrganizationClassIds: ["SA00003"], divisionName: "営業部", selectedEmployees: [], flowIds: ["13", "15"] },
  // 調達担当者確認: 事業視点・今後の見込み
  [ComfirmDivisions.procurementDivision]: { descendantOrganizationClassIds: ["PL00001"], divisionName: "調達部", selectedEmployees: [], flowIds: ["14", "15"] },
};

/**
 * purpose と isReviewMode から担当者設定対象のベース（ディープコピー）を返す。
 * 振り返りモード時は通常target（目的別）と振り返りtargetの flowIds を部署単位でマージする。
 * 同一部署の定義（descendant/parent/divisionName）は通常target側を優先する。
 * @param purpose 目的コード
 * @param isReviewMode 振り返りモードの場合は true
 */
const buildAssignTargetBase = (
  purpose: string | null | undefined,
  isReviewMode: boolean
): AssignTargetType => {
  const base: AssignTargetType = JSON.parse(JSON.stringify(
    purpose === PurposeType.NewProduct.code
      ? defaultAssignTargetNewProduct
      : defaultAssignTargetOther
  ));

  if (!isReviewMode) {
    return base;
  }

  // 振り返りモード時は振り返りtargetの flowIds を部署単位でマージ
  Object.keys(defaultAssignTargetReview).forEach(divisionId => {
    const reviewFlowIds = defaultAssignTargetReview[divisionId].flowIds ?? [];
    if (base[divisionId]) {
      // 既存部署に追加（重複排除）
      const existingFlowIds = base[divisionId].flowIds ?? [];
      base[divisionId].flowIds = Array.from(new Set([...existingFlowIds, ...reviewFlowIds]));
    } else {
      // 通常target に存在しない部署はディープコピーでそのまま追加
      base[divisionId] = JSON.parse(JSON.stringify(defaultAssignTargetReview[divisionId]));
    }
  });

  return base;
};

/**
 * 現行マッピングに基づいて有効な (flowId, divisionId) ペアを返す。
 * putApprover の未確認件数集計時、旧マッピングで残存しているワークフローデータを
 * 集計対象外とするためにFEからBEへ送信する。
 * @param purpose 目的コード
 * @param isReviewMode 振り返りモードの場合は true（振り返り3画面のマッピングも含める）
 */
export const getValidFlowDivisionPairs = (
  purpose: string | null | undefined,
  isReviewMode: boolean = false
): Array<{ flowId: string; divisionId: string }> => {
  const target = purpose === PurposeType.NewProduct.code
    ? defaultAssignTargetNewProduct
    : defaultAssignTargetOther;

  const pairs: Array<{ flowId: string; divisionId: string }> = [];
  Object.keys(target).forEach(divisionId => {
    (target[divisionId].flowIds ?? []).forEach(flowId => {
      pairs.push({ flowId, divisionId });
    });
  });

  // 振り返りモード時は振り返り3画面のペアも追加
  if (isReviewMode) {
    Object.keys(defaultAssignTargetReview).forEach(divisionId => {
      (defaultAssignTargetReview[divisionId].flowIds ?? []).forEach(flowId => {
        pairs.push({ flowId, divisionId });
      });
    });
  }

  return pairs;
};

/**
 * 振り返りモード用の (flowId, divisionId) ペアを返す。
 * 振り返り初期化時に BE の _copyWorkflow へ送信し、
 * flowIds 13/14/15 の派生レコードを生成するために使用する。
 */
export const getReviewFlowDivisionPairs = (): Array<{ flowId: string; divisionId: string }> => {
  const pairs: Array<{ flowId: string; divisionId: string }> = [];
  Object.keys(defaultAssignTargetReview).forEach(divisionId => {
    (defaultAssignTargetReview[divisionId].flowIds ?? []).forEach(flowId => {
      pairs.push({ flowId, divisionId });
    });
  });
  return pairs;
};

/**
 * 振り返りモード用の DEBUG 一括確認マップを返す。
 * BasicInfo.tsx の「全画面一括確認（DEBUG）」ボタン拡張用。
 * 戻り値: { flowId: [divisionName(ComfirmDivisionsのキー名), ...] }
 * 例: { "13": ["companyDivision", "salesDivision"], ... }
 */
export const getReviewDebugConfirmMap = (): Record<string, string[]> => {
  // divisionId (value) -> divisionName (key) の逆引きマップを作成
  const idToName: Record<string, string> = {};
  Object.entries(ComfirmDivisions).forEach(([name, id]) => {
    idToName[id] = name;
  });

  const map: Record<string, string[]> = {};
  Object.keys(defaultAssignTargetReview).forEach(divisionId => {
    const divisionName = idToName[divisionId];
    if (!divisionName) return;
    (defaultAssignTargetReview[divisionId].flowIds ?? []).forEach(flowId => {
      if (!map[flowId]) map[flowId] = [];
      map[flowId].push(divisionName);
    });
  });
  return map;
};

/**
 * ユーザーの子孫組織分類IDリストから確認部署IDを特定する。
 * @param userDescendantOrganizationClassIds ユーザーの子孫組織分類IDリスト
 * @param purpose 目的コード
 * @param isReviewMode 振り返りモードの場合は true
 * @returns 確認部署ID
 */
export const convertComfirmDivisionsId = (
  userDescendantOrganizationClassIds: string[],
  purpose: string | null | undefined,
  isReviewMode: boolean = false
) => {
  const assignTarget = buildAssignTargetBase(purpose, isReviewMode);

  // ユーザーの子孫組織分類IDリストが空の場合はundefinedを返す
  if (!userDescendantOrganizationClassIds || userDescendantOrganizationClassIds.length === 0) {
    return undefined;
  }

  return Object.keys(assignTarget).find(id => {
    const target = assignTarget[id];
    // parentOrganizationIds が定義されている場合（事業管理など）
    // 注：この場合はparentOrganizationIdで判定するため、ここでは別途処理が必要
    if (target.parentOrganizationIds && target.parentOrganizationIds.length > 0) {
      // parentOrganizationIdsの判定は呼び出し元で行う
      return false;
    }
    // descendantOrganizationClassIds が定義されている場合
    if (target.descendantOrganizationClassIds && target.descendantOrganizationClassIds.length > 0) {
      // ユーザーの子孫組織分類IDリストとカテゴリの子孫組織分類IDリストに共通の要素があるか
      return target.descendantOrganizationClassIds.some(classId =>
        userDescendantOrganizationClassIds.includes(classId)
      );
    }
    return false;
  });
}

/**
 * ユーザーの親組織IDから確認部署ID（事業管理）を特定する。
 * @param userParentOrganizationId ユーザーの親組織ID
 * @param purpose 目的コード
 * @param isReviewMode 振り返りモードの場合は true
 * @returns 確認部署ID（事業管理の場合のみ）
 */
export const convertComfirmDivisionsIdByParentOrganization = (
  userParentOrganizationId: string | null | undefined,
  purpose: string | null | undefined,
  isReviewMode: boolean = false
) => {
  const assignTarget = buildAssignTargetBase(purpose, isReviewMode);

  if (!userParentOrganizationId) {
    return undefined;
  }

  return Object.keys(assignTarget).find(id => {
    const target = assignTarget[id];
    // parentOrganizationIds が定義されている場合
    if (target.parentOrganizationIds && target.parentOrganizationIds.length > 0) {
      return target.parentOrganizationIds.includes(userParentOrganizationId);
    }
    return false;
  });
}

/**
 * 担当者一括設定画面
 */
const AssignTabModal: React.FC<any> = ({ isOpen, onClose, onCofirm, initSelectedEmployeeIds, initConfirmEmployees, disabledEmployeeIds, purpose, isReviewMode = false }) => {
  /** 翻訳 */
  const { t } = useTranslation();
  /** トースト表示用 */
  const toast = useToast();

  /** 通信オブジェクト */
  const WebAPI = useContext(WebAPIContext);

  /** 従業員マスタ情報 */
  const [employees, setEmployees] = useState<Employee[]>([]);

  /** 選択中の従業員IDリスト(仮渡し用) */
  const selectedEmployeeIds: string[] = [];

  const defaultError = { isError: false, errorMessage: "" };

  /** エラー情報 */
  const [error, SetError] = useState(JSON.parse(JSON.stringify(defaultError)));

  /** 担当者設定対象 */
  const [assignTarget, setAssignTarget] = useState<AssignTargetType>(buildAssignTargetBase(purpose, isReviewMode));

  /** 従業員情報（フィルター） */
  const filterdEmployees = (() => {
    const filtered = employees?.filter(e => {
      const allOrganizationClassIds = Object.values(assignTarget).flatMap(m => m.organizationClassIds ?? []);
      const allParentOrganizationIds = Object.values(assignTarget).flatMap(m => m.parentOrganizationIds ?? []);
      const allDescendantOrganizationClassIds = Object.values(assignTarget).flatMap(m => m.descendantOrganizationClassIds ?? []);
      const employeeDescendantClassIds = e.descendantOrganizationClassIds ?? [];
      return allOrganizationClassIds.includes(e.organizationClassId ?? "") ||
        allParentOrganizationIds.includes(e.parentOrganizationId ?? "") ||
        allDescendantOrganizationClassIds.some(classId => employeeDescendantClassIds.includes(classId));
    }) ?? [];
    // employeeIdで重複排除（最初にマッチしたレコードを残す）
    const seen = new Set<string>();
    return filtered.filter(e => {
      const id = e.employeeId ?? '';
      if (seen.has(id)) return false;
      seen.add(id);
      return true;
    });
  })();

  /**
   * 選択した従業員情報を指定部署に設定する。
   * @param id ID
   * @param selectedEmployees 選択した従業員情報
   */
  const changeSelectedEmployees = (id: string, selectedEmployees: Employee[]) => {
    console.log(`divisionId = ${id} - ${JSON.stringify(selectedEmployees)}`);
    setAssignTarget((prevState) => ({
      ...prevState,
      [id]: {
        ...prevState[id],
        selectedEmployees: selectedEmployees,
      }
    }));
  }

  /**
   * ＯＫボタンの非活性条件を判定する。
   * @returns true：未設定の部署がある。　false：すべての部署の担当者設定が完了している。
   */
  const isDisabledOk = () => {
    let isEabled = Object.keys(assignTarget).every(id => assignTarget[id].selectedEmployees!.length > 0);
    return !isEabled;
  };

  /**
   * 画面初回表示時
   */
  useEffect(() => {
    fetchData();
  }, []);

  /**
   * 画面表示時
   */
  useEffect(() => {
    if (isOpen) {
      // 振り返りモード時は通常targetに振り返り3画面の flowIds をマージ
      let updatedAssignTarget: AssignTargetType = buildAssignTargetBase(purpose, isReviewMode);

      // initConfirmEmployees（divisionId付き）が存在する場合はdivisionIdで直接振り分け
      if (initConfirmEmployees && Array.isArray(initConfirmEmployees) && initConfirmEmployees.length > 0) {
        Object.keys(updatedAssignTarget).forEach(divisionId => {
          // divisionIdが一致する確認者を抽出
          const matchedConfirmors = initConfirmEmployees.filter(
            (confirmor: { divisionId: string }) => confirmor.divisionId === divisionId
          );
          // employeeIdでemployeesから従業員情報を取得
          const selectedEmps = matchedConfirmors
            .map((confirmor: { employeeId: string }) =>
              employees.find(emp => emp.employeeId === confirmor.employeeId)
            )
            .filter((emp): emp is Employee => emp !== undefined);
          // employeeIdで重複排除
          const seen = new Set<string>();
          updatedAssignTarget[divisionId].selectedEmployees = selectedEmps.filter(e => {
            const empId = e.employeeId ?? '';
            if (seen.has(empId)) return false;
            seen.add(empId);
            return true;
          });
        });
      } else {
        // 従来のロジック（initSelectedEmployeeIdsのみの場合）
        Object.keys(updatedAssignTarget).forEach(id => {
          const target = updatedAssignTarget[id];
          const filtered = employees.filter(employee => {
            if (!initSelectedEmployeeIds?.includes(employee.employeeId)) return false;
            // descendantOrganizationClassIds が指定されている場合はそれで判定
            if (target.descendantOrganizationClassIds && target.descendantOrganizationClassIds.length > 0) {
              const employeeDescendantClassIds = employee.descendantOrganizationClassIds ?? [];
              return target.descendantOrganizationClassIds.some(classId => employeeDescendantClassIds.includes(classId));
            }
            // parentOrganizationIds が指定されている場合はそれで判定
            if (target.parentOrganizationIds && target.parentOrganizationIds.length > 0) {
              return target.parentOrganizationIds.includes(employee.parentOrganizationId ?? "");
            }
            // organizationClassIds が指定されている場合はそれで判定
            if (target.organizationClassIds && target.organizationClassIds.length > 0) {
              return target.organizationClassIds.includes(employee.organizationClassId ?? "");
            }
            return false;
          });
          // employeeIdで重複排除（最初にマッチしたレコードを残す）
          const seen = new Set<string>();
          updatedAssignTarget[id].selectedEmployees = filtered.filter(e => {
            const empId = e.employeeId ?? '';
            if (seen.has(empId)) return false;
            seen.add(empId);
            return true;
          });
        });
      }

      setAssignTarget(updatedAssignTarget);

      SetError(JSON.parse(JSON.stringify(defaultError)));
    }
  }, [isOpen, initConfirmEmployees]);

  /**
   * 従業員マスタ情報を取得する。
   */
  const fetchData = async () => {
    // 従業員情報取得
    const result = await getAssignEmployee(WebAPI.request);

    if (result) {
      setEmployees(result);
    } else {
      toast({
        title: t('common.message.initialDisplayError'),
        description: t('common.message.fetchEmployeesFailed'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  }

  /**
   * データ変換する。
   * @param assignTarget 設定対象（選択した従業員含む）
   */
  const convertFlowPersonData = (assignTarget: AssignTargetType) => {
    let flowPersonData: any[] = [];

    Object.keys(assignTarget).forEach(divisionId => {
      (assignTarget[divisionId].flowIds as string[]).forEach(flowId => {
        const tempData = (assignTarget[divisionId].selectedEmployees as Employee[]).map(employee => ({
          flowId: flowId,
          employeeId: employee.employeeId,
          divisionId: divisionId,
          employeeName: employee.employeeName,
        }));

        flowPersonData = [...flowPersonData, ...tempData];
      });
    });

    // employeeId + flowId + divisionId の組み合わせで重複排除（同一従業員でもカテゴリが異なれば別レコード）
    const seen = new Set<string>();
    flowPersonData = flowPersonData.filter(item => {
      const key = `${item.flowId}-${item.employeeId}-${item.divisionId}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });

    // ソート
    flowPersonData = flowPersonData.sort((item1, item2) => {
      if (item1.flowId < item2.flowId) return -1;
      if (item1.flowId > item2.flowId) return 1;
      return 0;
    });

    return flowPersonData;
  }

  /**
   * 本ポップアップを閉じ、「従業員別」タブの結果を呼び出し元に返す。
   */
  const handleTab1Ok = (selectedEmployees: Employee[]) => {
    const valid: string[] = [];

    Object.keys(assignTarget).forEach(id => {
      const target = assignTarget[id];
      let hasMatch = false;

      // descendantOrganizationClassIds が指定されている場合はそれで判定
      if (target.descendantOrganizationClassIds && target.descendantOrganizationClassIds.length > 0) {
        hasMatch = selectedEmployees.some(e => {
          const employeeDescendantClassIds = e.descendantOrganizationClassIds ?? [];
          return target.descendantOrganizationClassIds!.some(classId => employeeDescendantClassIds.includes(classId));
        });
      }
      // parentOrganizationIds が指定されている場合はそれで判定
      else if (target.parentOrganizationIds && target.parentOrganizationIds.length > 0) {
        hasMatch = target.parentOrganizationIds.some((parentId: string) => {
          return selectedEmployees.map(e => e.parentOrganizationId ?? "").includes(parentId);
        });
      }
      // organizationClassIds が指定されている場合はそれで判定
      else if (target.organizationClassIds && target.organizationClassIds.length > 0) {
        hasMatch = target.organizationClassIds.some((classId: string) => {
          return selectedEmployees.map(e => e.organizationClassId ?? "").includes(classId);
        });
      }

      if (!hasMatch && !valid.includes(id)) {
        valid.push(id);
      }
    });

    // エラー情報初期化
    SetError(JSON.parse(JSON.stringify(defaultError)));

    if (valid.length > 0) {
      // 入力担当者が不足（必要な部署が足りていない）
      const divisionName = valid.map(id => assignTarget[id].divisionName).join("、");

      SetError({
        isError: true,
        errorMessage: t('features.Approve.assignment.message.divisionEmployeeRequired', { divisionNames: divisionName }),
      });

      toast({
        title: t('common.label.inputError'),
        description: t('common.message.inputErrorContent'),
        status: 'warning',
        duration: 2000,
        isClosable: true,
      });
      return;
    }

    // 振り返りモード時は通常targetに振り返り3画面の flowIds をマージ
    let tempAssignTarget: AssignTargetType = buildAssignTargetBase(purpose, isReviewMode);

    Object.keys(assignTarget).forEach(id => {
      // 部署別に格納し直す。
      const target = assignTarget[id];
      const filtered = selectedEmployees.filter(e => {
        // descendantOrganizationClassIds が指定されている場合はそれで判定
        if (target.descendantOrganizationClassIds && target.descendantOrganizationClassIds.length > 0) {
          const employeeDescendantClassIds = e.descendantOrganizationClassIds ?? [];
          return target.descendantOrganizationClassIds.some(classId => employeeDescendantClassIds.includes(classId));
        }
        // parentOrganizationIds が指定されている場合はそれで判定
        if (target.parentOrganizationIds && target.parentOrganizationIds.length > 0) {
          return target.parentOrganizationIds.includes(e.parentOrganizationId ?? "");
        }
        // organizationClassIds が指定されている場合はそれで判定
        if (target.organizationClassIds && target.organizationClassIds.length > 0) {
          return target.organizationClassIds.includes(e.organizationClassId ?? "");
        }
        return false;
      });
      // employeeIdで重複排除（最初にマッチしたレコードを残す＝該当カテゴリの部署名を使用）
      const seen = new Set<string>();
      tempAssignTarget[id].selectedEmployees = filtered.filter(e => {
        const empId = e.employeeId ?? '';
        if (seen.has(empId)) return false;
        seen.add(empId);
        return true;
      });
    });

    const flowPersonData = convertFlowPersonData(tempAssignTarget);

    // 呼び出し元に返す。
    if (onCofirm) {
      onCofirm(flowPersonData);
    }

    onClose();
  };

  /**
   * 本ポップアップを閉じ、「部署別」タブの結果を呼び出し元に返す。
   */
  const handleTab2Ok = async () => {
    const flowPersonData = convertFlowPersonData(assignTarget);
    console.log(JSON.stringify(flowPersonData));

    // 呼び出し元に返す。
    if (onCofirm) {
      onCofirm(flowPersonData);
    }
    onClose();

  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} closeOnOverlayClick={false} isCentered scrollBehavior="inside" size="2xl">
      <ModalOverlay />
      <ModalContent maxHeight="90vh">
        <ModalHeader>
          <Flex align="center">
            {t('features.Approve.assignment.title.confirmHandler')}
            <Box ml={2} display="flex" alignItems="center">
              <HelpIcon size="24px" placement="bottom-start">
                <Text mb={2} fontWeight="normal" fontSize="sm">
                  {t('features.Approve.assignment.help.line1')}
                </Text>
                <Text mb={2} fontWeight="normal" fontSize="sm">
                  {t('features.Approve.assignment.help.line2')}
                </Text>
                <Text fontWeight="normal" fontSize="sm">
                  {t('features.Approve.assignment.help.line3')}
                </Text>
              </HelpIcon>
            </Box>
          </Flex>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Tabs>
            <TabList>
              {/* <Tab>{t('features.Approve.assignment.tab.byEmployee')}</Tab> */}
              <Tab>{t('features.Approve.assignment.tab.byDepartment')}</Tab>
            </TabList>
            <TabPanels>
              {/* <TabPanel> */}
              {/* {従業員別} */}
              {/* <AssignSelector
                  employees={filterdEmployees}
                  prevSelectedEmployeeIds={initSelectedEmployeeIds}
                  disabledEmployeeIds={disabledEmployeeIds}
                  onOk={handleTab1Ok}
                  error={error}
                /> */}
              {/* </TabPanel> */}
              <TabPanel>
                {/* {部署別} */}
                <Accordion allowMultiple>
                  {Object.keys(assignTarget).map(id => {
                    // 未定義を除外
                    const initSelectedEmployeeIds = assignTarget[id].selectedEmployees
                      ?.map(emp => emp.employeeId)
                      .filter((id): id is string => id !== undefined) ?? [];

                    return (
                      <AssignAccordionItem
                        key={id}
                        title={t(`common.constants.confirmDivisionShort.${id}`, { defaultValue: assignTarget[id].divisionName! })}
                        id={id}
                        changeSelectedEmployees={changeSelectedEmployees}
                        conditionOrganizationClassIds={assignTarget[id].organizationClassIds}
                        conditionParentOrganizationIds={assignTarget[id].parentOrganizationIds}
                        conditionDescendantOrganizationClassIds={assignTarget[id].descendantOrganizationClassIds}
                        employees={employees}
                        initSelectedEmployeeIds={initSelectedEmployeeIds}
                        disabledEmployeeIds={disabledEmployeeIds}
                      />
                    );
                  })}
                </Accordion>

                <Flex alignItems="center" justifyContent="right" marginTop={2}>
                  <Button colorScheme="blue" isDisabled={isDisabledOk()} onClick={handleTab2Ok}>{t('common.button.confirmAction')}</Button>
                </Flex>
              </TabPanel>
            </TabPanels>
          </Tabs>
        </ModalBody>
      </ModalContent>
    </Modal >
  );
};

export default AssignTabModal;
